import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Grid, Card, CardContent, Avatar, Tooltip
} from '@mui/material';
import { Refresh, Block, CheckCircle, Visibility, Search, FilterList, Delete, Edit, VerifiedUser } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/sanatani';

const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return 'https://api.bharatdarshan.app' + url;
};

const VERIFY_DURATIONS = [
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '1_year', label: '1 Year' },
  { value: 'lifetime', label: 'Lifetime' },
];

const SanataniUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [verifyDuration, setVerifyDuration] = useState('1_month');
  const [editForm, setEditForm] = useState({ name: '', city: '', state: '', bio: '', caste: '', gotra: '' });
  const [kutumbDialogOpen, setKutumbDialogOpen] = useState(false);
  const [kutumbMembers, setKutumbMembers] = useState([]);
  const [kutumbLoading, setKutumbLoading] = useState(false);
  const [kutumbOwnerName, setKutumbOwnerName] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;

      const res = await axios.get(`${API_BASE}/admin/users`, {
        params,
        headers: getAuthHeaders()
      });

      if (res.data.success) {
        setUsers(res.data.data || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
      }
    } catch (err) {
      setError('Error fetching users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, statusFilter]);

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  const handleViewUser = async (user) => {
    try {
      const res = await axios.get(`${API_BASE}/admin/users/${user._id}`, {
        headers: getAuthHeaders()
      });
      if (res.data.success) {
        setSelectedUser(res.data.data);
        setUserDialogOpen(true);
      }
    } catch (err) {
      setError('Error fetching user details: ' + err.message);
    }
  };

  const handleBanUser = (user) => {
    setSelectedUser(user);
    setBanReason('');
    setBanDialogOpen(true);
  };

  const confirmBan = async () => {
    try {
      await axios.put(
        `${API_BASE}/admin/users/${selectedUser._id}/ban`,
        { ban: !selectedUser.isBanned, reason: banReason },
        { headers: getAuthHeaders() }
      );
      setBanDialogOpen(false);
      setSuccessMsg(selectedUser.isBanned ? 'User unbanned' : 'User banned');
      fetchUsers();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Error updating user status: ' + err.message);
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE}/admin/users/${selectedUser._id}`, {
        headers: getAuthHeaders()
      });
      setDeleteDialogOpen(false);
      setSuccessMsg('User deleted successfully');
      fetchUsers();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Error deleting user: ' + err.message);
    }
  };

  const openKutumbList = async (user) => {
    const profileId = user?.profile?._id || user?._id;
    if (!profileId) return;
    setKutumbOwnerName(user.name || '');
    setKutumbDialogOpen(true);
    setKutumbLoading(true);
    setKutumbMembers([]);
    try {
      const res = await axios.get(`${API_BASE}/admin/users/${profileId}/kutumb-members`, { headers: getAuthHeaders() });
      setKutumbMembers(res.data?.data || []);
    } catch (err) {
      setError('Error loading kutumb members: ' + (err.response?.data?.message || err.message));
    } finally {
      setKutumbLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      city: user.city || '',
      state: user.state || '',
      bio: user.bio || '',
      caste: user.caste || '',
      gotra: user.gotra || '',
    });
    setEditDialogOpen(true);
  };

  const confirmEdit = async () => {
    try {
      await axios.put(
        `${API_BASE}/admin/users/${selectedUser._id}`,
        editForm,
        { headers: getAuthHeaders() }
      );
      setEditDialogOpen(false);
      setSuccessMsg('User updated successfully');
      fetchUsers();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Error updating user: ' + err.message);
    }
  };

  // Verification handlers
  const handleVerifyUser = (user) => {
    setSelectedUser(user);
    setVerifyDuration('1_month');
    setVerifyDialogOpen(true);
  };

  const confirmVerify = async () => {
    try {
      const isCurrentlyVerified = selectedUser.isVerified;
      await axios.put(
        `${API_BASE}/admin/users/${selectedUser._id}/verify`,
        {
          verify: !isCurrentlyVerified,
          duration: verifyDuration,
        },
        { headers: getAuthHeaders() }
      );
      setVerifyDialogOpen(false);
      setSuccessMsg(isCurrentlyVerified ? 'Verification removed' : `User verified for ${VERIFY_DURATIONS.find(d => d.value === verifyDuration)?.label || verifyDuration}`);
      fetchUsers();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError('Error updating verification: ' + err.message);
    }
  };

  const getVerificationInfo = (user) => {
    if (!user.isVerified) return null;
    const until = user.verifiedUntil ? new Date(user.verifiedUntil) : null;
    const isExpired = until && until < new Date();
    const durationLabel = VERIFY_DURATIONS.find(d => d.value === user.verifiedDuration)?.label || user.verifiedDuration || '';
    return { until, isExpired, durationLabel };
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
          {'\uD83D\uDC64'} Sanatani Manch Users
        </Typography>
        <IconButton onClick={fetchUsers} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search by name or mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <Search />
                  </IconButton>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="banned">Banned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" startIcon={<FilterList />} onClick={handleSearch}>
              Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FF9800' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Avatar</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Mobile</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>City</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Posts</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Verified</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Joined</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const vInfo = getVerificationInfo(user);
                  return (
                    <TableRow key={user._id} hover>
                      <TableCell>
                        <Avatar src={getFullImageUrl(user.profileImageUrl) || user.avatar} alt={user.name}>
                          {user.name?.charAt(0) || '?'}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {user.name || 'N/A'}
                          {user.isVerified && (
                            <VerifiedUser sx={{ fontSize: 16, color: '#1DA1F2' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{user.userId?.mobile || user.mobile || 'N/A'}</TableCell>
                      <TableCell>{user.city || 'N/A'}</TableCell>
                      <TableCell>{user.postsCount || user.postCount || 0}</TableCell>
                      <TableCell>
                        {user.isVerified ? (
                          <Tooltip title={vInfo ? `Until: ${vInfo.until?.toLocaleDateString('en-IN') || 'N/A'} (${vInfo.durationLabel})` : ''}>
                            <Chip
                              icon={<VerifiedUser sx={{ fontSize: 14 }} />}
                              label={vInfo?.durationLabel || 'Verified'}
                              size="small"
                              sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 'bold' }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="Not Verified" size="small" variant="outlined" sx={{ color: '#999' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isBanned ? 'Banned' : 'Active'}
                          color={user.isBanned ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{new Date(user.createdAt).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton color="primary" onClick={() => handleViewUser(user)} size="small">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isVerified ? 'Remove Verification' : 'Verify User'}>
                          <IconButton
                            sx={{ color: user.isVerified ? '#1DA1F2' : '#bbb' }}
                            onClick={() => handleVerifyUser(user)}
                            size="small"
                          >
                            <VerifiedUser />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit User">
                          <IconButton color="info" onClick={() => handleEditUser(user)} size="small">
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isBanned ? 'Unban' : 'Ban'}>
                          <IconButton
                            color={user.isBanned ? 'success' : 'warning'}
                            onClick={() => handleBanUser(user)}
                            size="small"
                          >
                            {user.isBanned ? <CheckCircle /> : <Block />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton color="error" onClick={() => handleDeleteUser(user)} size="small">
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No users found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, gap: 1 }}>
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Typography sx={{ alignSelf: 'center' }}>Page {page} of {totalPages}</Typography>
            <Button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </Box>
        </Paper>
      )}

      {/* User Detail Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF9800', color: 'white' }}>
          User Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedUser && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Avatar
                  src={getFullImageUrl(selectedUser.profile?.profileImageUrl || selectedUser.profileImageUrl) || selectedUser.avatar}
                  sx={{ width: 120, height: 120, mx: 'auto' }}
                >
                  {(selectedUser.profile?.name || selectedUser.name || '?').charAt(0)}
                </Avatar>
                {(selectedUser.profile?.isVerified || selectedUser.isVerified) && (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Chip
                      icon={<VerifiedUser sx={{ fontSize: 16 }} />}
                      label="Verified"
                      sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 'bold' }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography><b>Name:</b> {selectedUser.profile?.name || selectedUser.name || 'N/A'}</Typography>
                    <Typography><b>Mobile:</b> {selectedUser.profile?.userId?.mobile || selectedUser.userId?.mobile || 'N/A'}</Typography>
                    <Typography><b>City:</b> {selectedUser.profile?.city || selectedUser.city || 'N/A'}</Typography>
                    <Typography><b>State:</b> {selectedUser.profile?.state || selectedUser.state || 'N/A'}</Typography>
                    <Typography><b>Gotra:</b> {selectedUser.profile?.gotra || selectedUser.gotra || 'N/A'}</Typography>
                    <Typography><b>Caste:</b> {selectedUser.profile?.caste || selectedUser.caste || 'N/A'}</Typography>
                    <Typography><b>Bio:</b> {selectedUser.profile?.bio || selectedUser.bio || 'N/A'}</Typography>
                    <Typography><b>Profile Visibility:</b> {selectedUser.profile?.profileVisibility || selectedUser.profileVisibility || 'public'}</Typography>
                    {(selectedUser.profile?.isVerified || selectedUser.isVerified) && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#E3F2FD', borderRadius: 1 }}>
                        <Typography sx={{ color: '#1565C0', fontWeight: 'bold' }}>
                          {'\u2705'} Verified Profile
                        </Typography>
                        <Typography variant="body2">
                          Duration: {VERIFY_DURATIONS.find(d => d.value === (selectedUser.profile?.verifiedDuration || selectedUser.verifiedDuration))?.label || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          Until: {(selectedUser.profile?.verifiedUntil || selectedUser.verifiedUntil) ? new Date(selectedUser.profile?.verifiedUntil || selectedUser.verifiedUntil).toLocaleDateString('en-IN') : 'N/A'}
                        </Typography>
                      </Box>
                    )}
                    {selectedUser.stats && (
                      <Box sx={{ mt: 1 }}>
                        <Chip label={`Posts: ${selectedUser.stats.posts || 0}`} sx={{ mr: 0.5, mb: 0.5 }} size="small" />
                        <Chip label={`Followers: ${selectedUser.stats.followers || 0}`} sx={{ mr: 0.5, mb: 0.5 }} size="small" />
                        <Chip label={`Following: ${selectedUser.stats.following || 0}`} sx={{ mr: 0.5, mb: 0.5 }} size="small" />
                        <Chip label={`Kutumb: ${selectedUser.stats.kutumbMembers || 0}`} sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }} size="small" color="primary" variant="outlined" onClick={() => openKutumbList(selectedUser)} />
                        <Chip label={`Flags: ${selectedUser.stats.timesFlagged || 0}`} sx={{ mr: 0.5, mb: 0.5 }} size="small" color={selectedUser.stats.timesFlagged > 0 ? 'error' : 'default'} />
                      </Box>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Joined: {new Date(selectedUser.profile?.createdAt || selectedUser.createdAt).toLocaleString('en-IN')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#2196F3', color: 'white' }}>
          {'\u270F\uFE0F'} Edit User: {selectedUser?.name}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name" value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="City" value={editForm.city}
                onChange={(e) => setEditForm({...editForm, city: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="State" value={editForm.state}
                onChange={(e) => setEditForm({...editForm, state: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Caste" value={editForm.caste}
                onChange={(e) => setEditForm({...editForm, caste: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Gotra" value={editForm.gotra}
                onChange={(e) => setEditForm({...editForm, gotra: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Bio" value={editForm.bio}
                onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirmEdit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify User Dialog */}
      <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: selectedUser?.isVerified ? '#f44336' : '#1DA1F2', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUser />
            {selectedUser?.isVerified ? 'Remove Verification' : 'Verify User Profile'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedUser?.isVerified ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will remove the verified badge from <b>{selectedUser?.name}</b>'s profile.
                The blue tick will no longer appear next to their name in the app.
              </Alert>
              <Typography variant="body2" color="textSecondary">
                Current verification: {VERIFY_DURATIONS.find(d => d.value === selectedUser?.verifiedDuration)?.label || selectedUser?.verifiedDuration || 'N/A'}
              </Typography>
              {selectedUser?.verifiedUntil && (
                <Typography variant="body2" color="textSecondary">
                  Expires: {new Date(selectedUser.verifiedUntil).toLocaleDateString('en-IN')}
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Verifying <b>{selectedUser?.name}</b> will add a blue tick ({'\u2713'}) badge next to their name,
                similar to verified profiles on Facebook, Instagram, and Twitter.
              </Alert>
              <Typography sx={{ mb: 2 }}>
                Select verification duration:
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Duration</InputLabel>
                <Select
                  value={verifyDuration}
                  label="Duration"
                  onChange={(e) => setVerifyDuration(e.target.value)}
                >
                  {VERIFY_DURATIONS.map((d) => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                {'\u2139\uFE0F'} After the duration expires, the verification will be automatically removed
                and the user's profile will return to normal.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{
              bgcolor: selectedUser?.isVerified ? '#f44336' : '#1DA1F2',
              '&:hover': { bgcolor: selectedUser?.isVerified ? '#d32f2f' : '#1a8cd8' }
            }}
            onClick={confirmVerify}
          >
            {selectedUser?.isVerified ? 'Remove Verification' : 'Verify User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ban Confirmation Dialog */}
      <Dialog open={banDialogOpen} onClose={() => setBanDialogOpen(false)}>
        <DialogTitle>
          {selectedUser?.isBanned ? 'Unban User' : 'Ban User'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to {selectedUser?.isBanned ? 'unban' : 'ban'} <b>{selectedUser?.name}</b>?
          </Typography>
          {!selectedUser?.isBanned && (
            <TextField
              fullWidth
              label="Ban Reason"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              multiline rows={2}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={selectedUser?.isBanned ? 'success' : 'error'}
            onClick={confirmBan}
          >
            {selectedUser?.isBanned ? 'Unban' : 'Ban'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: '#f44336' }}>{'\u26A0\uFE0F'} Delete User Permanently</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete the user and ALL their data including posts, comments, likes, chats, and followers!
          </Alert>
          <Typography>
            Are you sure you want to delete <b>{selectedUser?.name}</b> ({selectedUser?.city})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kutumb Members Dialog */}
      <Dialog open={kutumbDialogOpen} onClose={() => setKutumbDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF6B35', color: 'white' }}>
          Kutumb Members - {kutumbOwnerName}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {kutumbLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : kutumbMembers.length === 0 ? (
            <Alert severity="info">No Kutumb members added yet.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#FFF3E0' }}>
                    <TableCell>Photo</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Relation</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>DOB</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kutumbMembers.map((m) => (
                    <TableRow key={m._id} hover>
                      <TableCell>
                        <Avatar src={getFullImageUrl(m.photo || m.linkedProfile?.profilePhoto)} sx={{ width: 36, height: 36 }}>
                          {(m.name || '?').charAt(0)}
                        </Avatar>
                      </TableCell>
                      <TableCell>{m.title ? `${m.title} ` : ''}{m.name}</TableCell>
                      <TableCell><Chip label={m.relation} size="small" /></TableCell>
                      <TableCell>{m.mobile || m.linkedProfile?.mobile || '-'}</TableCell>
                      <TableCell>{m.dateOfBirth ? new Date(m.dateOfBirth).toLocaleDateString('en-IN') : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKutumbDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SanataniUsers;
